import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardHeader } from "../../sections/teacher/DashboardHeader";
import { InstructorSidebar } from "../../sections/teacher/InstructorSidebar";
import { Overview } from "../../sections/teacher/Overview";
import GradeDistributionChart from "../../sections/teacher/grade-distribution/GradeDistributionChart";
import GradeDistributionFilters, {
  FilterState,
} from "../../sections/teacher/grade-distribution/GradeDistributionFilters";
import { StudentsProgress } from "../../sections/teacher/student-progress/StudentsProgress";
import CreatePeAssignmentModal from "../../sections/teacher/CreatePeAssignmentModal";
import InstructorSettingsModal from "../../sections/SettingsModal";
import ClassManagement from "../../sections/teacher/ClassManagement";
import AssignmentsSection from "../../sections/teacher/AssignmentsSection";
// Run E2 J1: student-teacher chat removed (K-5). ChatList is no longer shown.
import TeacherInformation from "../../sections/teacher/TeacherInformation";
import GradeScalesManager from "./GradeScalesManager";
import Gradebook from "../../sections/teacher/Gradebook";
import { useT } from "../../../stores/uiLang";
// Run E2 J6 (D10): teacher review queue outside Canvas.
import HitlQueue from "../pe-serving/HitlQueue";

// Run E2 J2: PFL tabs hidden for Project Explore: Guidebooks (PFL state
// standards), Scaffolding Settings, Interview Settings and the PFL Assessment
// Library (?assessment-library=true). The section files are kept.
const DASH_T = {
  en: { assign: "Assign PE Chapter", chooseClass: "Choose a Class" },
  es: { assign: "Asignar un capítulo", chooseClass: "Elige una clase" },
};
export type TabType =
  | "overview"
  | "class-management"
  | "students"
  | "assignments"
  | "grade-scales"
  | "gradebook"
  | "review";

function TeacherDashboard() {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [isPeAssignOpen, setIsPeAssignOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const t = useT(DASH_T);
  const [overviewFilters, setOverviewFilters] = useState<FilterState>({
    selectedClass: "",
  });

  useEffect(() => {
    // If manage query param is present, set active tab to class-management
    if (searchParams.get("manage")) {
      setActiveTab("class-management");
    }
  }, [searchParams]);

  // Run E2 J1: student-teacher chat removed (K-5). No chat room fetch.

  return (
    <div className="min-h-screen pt-[72px]">
      <div className="flex min-h-[calc(100vh-72px)]">
        <InstructorSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isMobileOpen={isMobileSidebarOpen}
          onMobileClose={() => setIsMobileSidebarOpen(false)}
          onMobileOpen={() => setIsMobileSidebarOpen(true)}
        />

        <main className="flex-1 overflow-auto bg-gray-50">
          <DashboardHeader
            onOpenSettings={() => setIsSettingsModalOpen(true)}
            onToggleMobileMenu={() => setIsMobileSidebarOpen(true)}
          />
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <TeacherInformation className="mb-6" />

            <div className="mb-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsPeAssignOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                {t.assign}
              </button>
            </div>

            {activeTab === "overview" && (
              <>
                <GradeDistributionFilters
                  onFiltersChange={setOverviewFilters}
                  title={t.chooseClass}
                />
                <Overview filters={overviewFilters} />
                <GradeDistributionChart filters={overviewFilters} />
              </>
            )}
            {activeTab === "class-management" && <ClassManagement />}
            {activeTab === "students" && <StudentsProgress />}
            {activeTab === "assignments" && <AssignmentsSection />}
            {activeTab === "grade-scales" && <GradeScalesManager />}
            {activeTab === "gradebook" && <Gradebook />}
            {activeTab === "review" && <HitlQueue embedded />}
          </div>
        </main>
      </div>

      <CreatePeAssignmentModal
        isOpen={isPeAssignOpen}
        onClose={() => setIsPeAssignOpen(false)}
      />

      <InstructorSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </div>
  );
}

export default TeacherDashboard;
